/**
 * AdRewardWidget — T10 «Reward-type effectiveness»
 * =====================================================================
 *
 * Спека: .claude/plans/2026-08-25-avz-ad-monetization-dashboards.md, T10.
 *
 * ОПИСАТЕЛЬНАЯ СТАТИСТИКА, БЕЗ ПРИЧИННЫХ ВЫВОДОВ: сравниваем next-day
 * return rate игроков, использовавших тип награды, с рекламно-активными
 * игроками, которые его не использовали (dumbbell-график) — совпадение
 * не означает эффект самой награды (самоотбор игроков).
 *
 * Строки с users < 500 сервер уже подавил (порог из спеки); их список —
 * в data.suppressedRewards, показываем как сноску.
 *
 * Клик по строке таблицы открывает drill-down по reward_name (p4str,
 * заполнен ~51%) — только там, где он не пуст.
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useAdsWidgetData } from '../../../hooks/useAdsGeo';
import { AdsService } from '../../../services/adsService';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../ui/table';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Legend, ZAxis } from 'recharts';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../../ui/responsive-chart';
import { adsSources as adsSourcesSpecs } from '@ggstat/chart-specs';

const [NOT_USED_SERIES, USED_SERIES] = adsSourcesSpecs.rewardsDumbbell.series;
const USED_COLOR = USED_SERIES.color;
const NOT_USED_COLOR = NOT_USED_SERIES.color;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtPct = (v) => (v == null ? '—' : `${(Number(v) * 100).toFixed(1)}%`);
const fmtUsd4 = (v) => `$${Number(v || 0).toFixed(4)}`;

/** Точки dumbbell-графика: по одной строке-паре (использовал / не использовал) на reward, y — категория. */
function buildDumbbellRows(rows) {
  return (rows || [])
    .filter((r) => r.usedReturnRate != null)
    .map((r) => ({
      reward: r.reward,
      used: r.usedReturnRate,
      notUsed: r.notUsedReturnRate
    }));
}

function DumbbellTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm space-y-1">
      <p className="font-medium">{p.reward}</p>
      <p style={{ color: USED_COLOR }}>{USED_SERIES.label}: {fmtPct(p.used)}</p>
      <p style={{ color: NOT_USED_COLOR }}>{NOT_USED_SERIES.label}: {fmtPct(p.notUsed)}</p>
    </div>
  );
}

export function AdRewardWidget() {
  const [drillReward, setDrillReward] = useState(null);

  const effectivenessData = useAdsWidgetData(
    async (dashboardFilters, options) => AdsService.get('/rewards', dashboardFilters, {}, options),
    []
  );

  const widgetProps = useBaseWidget(effectivenessData);
  const { data } = widgetProps;

  const drillData = useAdsWidgetData(
    async (dashboardFilters, options) => {
      if (!drillReward) return null;
      return AdsService.get('/rewards', dashboardFilters, { reward: drillReward }, options);
    },
    [drillReward]
  );

  const dumbbellRows = useMemo(() => buildDumbbellRows(data?.rows), [data?.rows]);
  const rewardCategories = useMemo(() => dumbbellRows.map((r) => r.reward), [dumbbellRows]);

  const usedPoints = useMemo(
    () => dumbbellRows.map((r) => ({ reward: r.reward, y: rewardCategories.indexOf(r.reward), x: r.used, used: r.used, notUsed: r.notUsed })),
    [dumbbellRows, rewardCategories]
  );
  const notUsedPoints = useMemo(
    () => dumbbellRows.map((r) => ({ reward: r.reward, y: rewardCategories.indexOf(r.reward), x: r.notUsed, used: r.used, notUsed: r.notUsed })),
    [dumbbellRows, rewardCategories]
  );

  return (
    <BaseWidget
      {...widgetProps}
      title="Reward-type effectiveness"
      subtitle="Описательная статистика: совпадение метрик не означает причинного эффекта награды"
      emptyMessage="Нет данных за выбранный диапазон (или все типы наград ниже порога n≥500)"
      skeletonRows={10}
    >
      <div className="space-y-6">
        {data?.note && (
          <p className="text-xs text-muted-foreground bg-muted/50 rounded p-2">{data.note}</p>
        )}

        {/* Dumbbell: next-day return rate, использовал vs не использовал */}
        <div>
          <p className="text-sm font-medium mb-2">Next-day return rate: использовал награду vs не использовал</p>
          {dumbbellRows.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(220, dumbbellRows.length * 32)}>
              <ScatterChart margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  type="number"
                  dataKey="x"
                  domain={[0, 1]}
                  tickFormatter={(v) => `${Math.round(v * 100)}%`}
                  className="text-xs"
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  tickFormatter={(v) => rewardCategories[v] || ''}
                  ticks={rewardCategories.map((_, idx) => idx)}
                  interval={0}
                  width={160}
                  className="text-xs"
                />
                <ZAxis range={[80, 80]} />
                <Tooltip content={<DumbbellTooltip />} />
                <Legend />
                <Scatter name={NOT_USED_SERIES.label} data={notUsedPoints} fill={NOT_USED_COLOR} isAnimationActive={false} />
                <Scatter name={USED_SERIES.label} data={usedPoints} fill={USED_COLOR} isAnimationActive={false} />
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-6 text-sm text-muted-foreground">Нет данных</div>
          )}
        </div>

        {/* Таблица: остальные метрики + payer rate */}
        <div>
          <p className="text-sm font-medium mb-2">
            Все метрики по типу награды (клик по строке — drill-down по reward_name, где заполнен)
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Тип награды</TableHead>
                <TableHead className="text-right">Пользователи</TableHead>
                <TableHead className="text-right">Доля рекл. аудитории</TableHead>
                <TableHead className="text-right">Показов/польз.</TableHead>
                <TableHead className="text-right">Выручка/польз.</TableHead>
                <TableHead className="text-right">30д payer rate (использовал)</TableHead>
                <TableHead className="text-right">30д payer rate (не использовал)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.rows || []).map((row) => (
                <TableRow
                  key={row.reward}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setDrillReward(row.reward === drillReward ? null : row.reward)}
                >
                  <TableCell className="font-medium">{row.reward}</TableCell>
                  <TableCell className="text-right">{fmtInt(row.users)}</TableCell>
                  <TableCell className="text-right">{fmtPct(row.shareOfAdUsers)}</TableCell>
                  <TableCell className="text-right">{row.impsPerUser.toFixed(2)}</TableCell>
                  <TableCell className="text-right">{fmtUsd4(row.revenuePerUser)}</TableCell>
                  <TableCell className="text-right">{fmtPct(row.usedPayerRate)}</TableCell>
                  <TableCell className="text-right">
                    {fmtPct(row.notUsedPayerRate)} <span className="text-muted-foreground">(n={fmtInt(row.notUsedN)})</span>
                  </TableCell>
                </TableRow>
              ))}
              {(!data?.rows || data.rows.length === 0) && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                    Нет данных
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {data?.suppressedRewards?.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              Скрыто {data.suppressedRewards.length} типов награды с числом пользователей меньше {data.minUsersThreshold}: {data.suppressedRewards.join(', ')}
            </p>
          )}
        </div>

        {/* Drill-down по reward_name (p4str) */}
        {drillReward && (
          <div>
            <p className="text-sm font-medium mb-2">
              Drill-down по reward_name для «{drillReward}» (p4str, заполнен частично — только непустые значения)
            </p>
            {drillData.loading && <p className="text-sm text-muted-foreground">Загрузка...</p>}
            {!drillData.loading && (drillData.data?.names?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>reward_name</TableHead>
                    <TableHead className="text-right">Пользователи</TableHead>
                    <TableHead className="text-right">Показы</TableHead>
                    <TableHead className="text-right">Выручка</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillData.data.names.map((n) => (
                    <TableRow key={n.rewardName}>
                      <TableCell>{n.rewardName}</TableCell>
                      <TableCell className="text-right">{fmtInt(n.users)}</TableCell>
                      <TableCell className="text-right">{fmtInt(n.imps)}</TableCell>
                      <TableCell className="text-right">${n.revenue.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground">
                reward_name пуст для всех показов этого типа награды в выбранном окне.
              </p>
            ))}
          </div>
        )}
      </div>
    </BaseWidget>
  );
}

export default AdRewardWidget;
